import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useParams } from 'react-router-dom';
import { Skeleton, TablePaginationConfig } from 'antd';
import { t } from 'i18next';
import { useSetRecoilState } from 'recoil';
import { useUpdateEffect } from 'usehooks-ts';

import './index.less';

import { ProjectCreationRequest, ProjectModel, ProjectUpdatingRequest } from '../../../../gamma-api';
import { defaultApi } from '../../../apis';
import { ValidationErrors } from '../../../types';
import { tryGetValidationErrors } from '../../../utils/errorUtils';
import { getPageFromSearchParams, PAGE_INDEX_PARAM } from '../../../utils/urlUtil';
import { getProjectPath } from '../../../utils/pathUtils';
import { ProjectsEmptyLogo } from '../../../assets';
import { alertsSelectorAdd } from '../../../recoil';
import IbButton, { IIbButtonAction } from '../../../components/common/IbButton';
import IbPageTitle from '../../../components/IbPageTitle';
import IbTypography from '../../../components/common/IbTypography';
import IbIcon from '../../../components/common/IbIcon';
import IbPagination from '../../../components/common/IbPagination';
import ImportUpload from '../../../components/ImportUpload';

import {
  MAIN_CLASS_NAME,
  EMPTY_CONTENT_PLACEHOLDER_CLASS_NAME,
  CONTENT_CLASS_NAME,
  EMPTY_CONTENT_CLASS_NAME,
  SKELETON_CLASS_NAME,
  SKELETON_ROW_COUNT,
  SKELETON_ROW_CLASS_NAME,
  SKELETON_COLUMN_COUNT,
  GRID_CLASS_NAME,
  NOTHING_FOUND_PLACEHOLDER_CLASS_NAME,
  PAGINATION_ROW_CLASS_NAME,
  TOOLBAR_CLASSNAME,
} from './const';
import ProjectCard from './ProjectCard';
import ProjectModal from './ProjectModal';
import ConfirmModal from './ConfirmModal';

const PROJECTS_PAGE_SIZE = 30; // NOTE: Should be divisible by 2 and 3 for grid layout in different breakpoints.

const ProjectsPage: React.FC = () => {
  const location = useLocation();
  const { push } = useHistory();

  const { folderId } = useParams<{ folderId: string }>();

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: getPageFromSearchParams(location.search),
    pageSize: PROJECTS_PAGE_SIZE,
    total: 0,
  });

  const [searchText, setSearchText] = useState('');
  const [projects, setProjects] = useState<ProjectModel[]>([]);
  const [loading, setLoading] = useState(true);

  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
  const [projectModalVisible, setProjectModalVisible] = useState(false);

  const [updateProject, setUpdateProject] = useState<ProjectModel>();
  const [deleteProject, setDeleteProject] = useState<ProjectModel>();

  const projectListIsEmpty = !projects.length;
  const showEmptyList = projectListIsEmpty && !loading;

  const loadProjects = async (withLoading: boolean) => {
    try {
      withLoading && setLoading(true);
      const response = await defaultApi.searchProjectsDetailed(
        searchText,
        [folderId],
        (pagination.current || 1) - 1,
        PROJECTS_PAGE_SIZE,
        true
      );
      setProjects(response.data.items || []);
      setPagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (err) {
      addAlert({ content: t('Failed to load projects'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      withLoading && setLoading(false);
    }
  };

  const goToProject = (id: string) => {
    push(getProjectPath(folderId, id));
  };

  useEffect(() => {
    loadProjects(true).finally();
  }, [folderId, pagination.current, pagination.pageSize, searchText]);

  useEffect(() => {
    const page = getPageFromSearchParams(location.search);
    if (page !== pagination.current) {
      setPagination((prev) => ({ ...prev, current: page }));
    }
  }, [location.search]);

  useUpdateEffect(() => {
    if (pagination.current !== 1) {
      setPagination({ ...pagination, current: 1 });
    }
  }, [searchText]);

  useUpdateEffect(() => {
    if (pagination.current) {
      const params = new URLSearchParams(location.search);
      params.set(PAGE_INDEX_PARAM, pagination.current.toString());
      push({ pathname: location.pathname, search: params.toString() });
    }
  }, [pagination.current]);

  useEffect(() => {
    if (searchText) {
      setSearchText('');
    } else if (pagination.current !== 1) {
      setPagination({ ...pagination, current: 1 });
    }
  }, [folderId]);

  const onCreateProjectClick = () => {
    setUpdateProject(undefined);
    setProjectModalVisible(true);
  };

  const onUpdateProjectClick = (project: ProjectModel) => () => {
    setUpdateProject(project);
    setProjectModalVisible(true);
  };

  const onDeleteProjectClick = (project: ProjectModel) => () => {
    setDeleteProject(project);
  };

  const onImportFileFinish = async (project: ProjectModel) => {
    goToProject(project.project_id);
  };

  const onProjectModalAdd = async (
    project: ProjectCreationRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ): Promise<void> => {
    try {
      const response = await defaultApi.createProjectGeneralProjectsPost(project);
      setProjectModalVisible(false);
      goToProject(response.data.project_id);
    } catch (e) {
      const validationErrors = tryGetValidationErrors(e as Error);

      if (validationErrors) {
        onSubmitErrors(validationErrors);
      } else {
        addAlert({
          content: t('Projects Page: Project creating error'),
          type: 'error',
          icon: <IbIcon iconName="attention" />,
        });
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  };

  const onProjectModalEdit = async (
    project: ProjectUpdatingRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ): Promise<void> => {
    if (!updateProject) return;

    try {
      await defaultApi.updateProjectGeneralProjectsProjectIdPut(updateProject.project_id, project);
      await loadProjects(false);
      setProjectModalVisible(false);
    } catch (e) {
      const validationErrors = tryGetValidationErrors(e as Error);

      if (validationErrors) {
        onSubmitErrors(validationErrors);
      } else {
        addAlert({
          content: t('Projects Page: Project updating error'),
          type: 'error',
          icon: <IbIcon iconName="attention" />,
        });
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  };

  const onProjectModalCancel = () => {
    setProjectModalVisible(false);
  };

  const onDeleteConfirmButtonClick = async () => {
    if (!deleteProject) return;

    setDeleteConfirmLoading(true);
    try {
      await defaultApi.deleteOneProjectGeneralProjectsProjectIdDelete(deleteProject.project_id);
      await loadProjects(false);
    } catch (e) {
      addAlert({
        content: t('Projects Page: Project deletion error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
    }
    setDeleteConfirmLoading(false);
    setDeleteProject(undefined);
  };

  const onDeleteCancelButtonClick = () => {
    setDeleteProject(undefined);
  };

  const onPaginationChange = (page: number) => setPagination({ ...pagination, current: page });

  const onSearchTextChange = (value: string) => setSearchText(value);

  const renderToolbar = () => {
    if (showEmptyList) {
      return (
        <div className={TOOLBAR_CLASSNAME}>
          <IbButton icon={<IbIcon iconName="add-one" />} onClick={onCreateProjectClick}>
            {t('Projects Page: Toolbar: Create')}
          </IbButton>
          <ImportUpload.ProjectImport folderId={folderId} onFinish={onImportFileFinish}>
            <IbButton icon={<IbIcon iconName="afferent-three" />} type="secondary">
              {t('Projects Page: Toolbar: Import')}
            </IbButton>
          </ImportUpload.ProjectImport>
        </div>
      );
    }

    const actions: IIbButtonAction[] = [
      {
        icon: <IbIcon iconName="afferent-three" />,
        text: t('Projects Page: Toolbar: Import'),
        render: (child) => (
          <ImportUpload.ProjectImport folderId={folderId} onFinish={onImportFileFinish}>
            {child}
          </ImportUpload.ProjectImport>
        ),
      },
    ];

    return (
      <div className={TOOLBAR_CLASSNAME}>
        <IbButton actions={actions} icon={<IbIcon iconName="add-one" />} onClick={onCreateProjectClick}>
          {t('Projects Page: Toolbar: Create')}
        </IbButton>
      </div>
    );
  };

  const renderProjectList = () => (
    <>
      {renderToolbar()}
      <div className={GRID_CLASS_NAME}>
        {projects.map((project) => (
          <ProjectCard
            key={project.project_id}
            project={project}
            searchText={searchText}
            onDeleteProject={onDeleteProjectClick(project)}
            onEditProject={onUpdateProjectClick(project)}
            onProjectClick={() => goToProject(project.project_id)}
          />
        ))}
      </div>
    </>
  );

  const renderEmptyList = () => {
    if (searchText) {
      return <div className={NOTHING_FOUND_PLACEHOLDER_CLASS_NAME}>{t('Projects: Nothing found')}</div>;
    }

    return (
      <div className={EMPTY_CONTENT_PLACEHOLDER_CLASS_NAME}>
        <ProjectsEmptyLogo />
        <IbTypography>
          <IbTypography.Paragraph disabled type="secondary">
            {t('Here you can create a smart bot project')}
            <br />
            {t('with artificial intelligence')}
            <br />
            {t('trained on your database')}
          </IbTypography.Paragraph>
        </IbTypography>
        {renderToolbar()}
      </div>
    );
  };

  // TODO: replace with 'react-loading-skeleton' or adjust animation colors for similar look.
  const skeleton = (
    <div className={SKELETON_CLASS_NAME}>
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, row) => (
        <div key={row} className={SKELETON_ROW_CLASS_NAME}>
          {Array.from({ length: SKELETON_COLUMN_COUNT }).map((_, col) => (
            <Skeleton key={col} active />
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbPageTitle
        showSearchInput
        searchText={searchText}
        title={t('Projects Page: Title')}
        onDebouncedSearch={onSearchTextChange}
      />
      <div className={`${CONTENT_CLASS_NAME} ${showEmptyList && !searchText ? EMPTY_CONTENT_CLASS_NAME : ''}`}>
        {loading ? skeleton : projectListIsEmpty ? renderEmptyList() : renderProjectList()}
      </div>
      {(pagination.total ?? 0) > PROJECTS_PAGE_SIZE && (
        <div className={PAGINATION_ROW_CLASS_NAME}>
          <IbPagination
            current={pagination.current}
            defaultCurrent={1}
            defaultPageSize={PROJECTS_PAGE_SIZE}
            pageSize={PROJECTS_PAGE_SIZE}
            showSizeChanger={false}
            total={pagination.total}
            onChange={onPaginationChange}
          />
        </div>
      )}
      <ProjectModal
        editProject={updateProject}
        visible={projectModalVisible}
        onAdd={onProjectModalAdd}
        onCancel={onProjectModalCancel}
        onEdit={onProjectModalEdit}
      />
      <ConfirmModal.DeleteProject
        loading={deleteConfirmLoading}
        visible={!!deleteProject}
        onCancel={onDeleteCancelButtonClick}
        onConfirm={onDeleteConfirmButtonClick}
      />
    </div>
  );
};

export default ProjectsPage;
